"use client";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Button } from "@heroui/button";
import { Tooltip } from "@heroui/react";
import { HandThumbsUp, HandThumbsDown } from "react-bootstrap-icons";
import { cn } from "@heroui/react";

import { logger } from "@/lib/secure-logger";
import { FeedbackModal } from "@/components/FeedbackModal";
import { findFirstQuestion } from "@/lib/journey-utils";

import LiveSummary from "./onboarding/LiveSummary";
import OnboardingWizard from "./onboarding/OnboardingWizard";
import { sendPageView } from "./helpers/user-actions";
import { useJourneyData } from "./JourneyDataClientProvider";
import { useAnswerContext } from "./onboarding/AnswerContext";

function ClientHomePage() {
  const { userData } = useJourneyData();
  const {
    dismissWelcomeScreen,
    currentQuestionId: contextCurrentQuestionId,
    answers: contextAnswers,
  } = useAnswerContext();

  const [currentIllustration, setCurrentIllustration] = useState<string | null>(
    null,
  );
  const [showWelcome, setShowWelcome] = useState(false);
  const [isWelcomeExiting, setIsWelcomeExiting] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<
    "positive" | "negative" | null
  >(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [modalInitialRating, setModalInitialRating] = useState<
    "positive" | "negative" | null
  >(null);
  const welcomeCheckedRef = useRef(false);
  const getStartedButtonRef = useRef<HTMLButtonElement>(null);

  const handleFeedbackClick = async (type: "positive" | "negative") => {
    setFeedbackGiven(type);
    setModalInitialRating(type);

    // Send feedback phase 1 to API immediately (like footer does)
    try {
      const feedbackData = {
        phase1: type,
        sessionId:
          localStorage.getItem("crowdsec_session_id") ||
          `session_${Date.now()}`,
        timestamp: new Date().toISOString(),
        userJourney: {
          answers: contextAnswers,
          currentQuestionId: contextCurrentQuestionId,
          progressPercent: Object.keys(contextAnswers).length * 10, // Rough estimate
        },
      };

      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(feedbackData),
      });

      if (response.ok) {
        logger.info("Homepage footer feedback sent", {
          type,
          source: "homepage_footer",
          answersCount: Object.keys(contextAnswers).length,
          currentQuestion: contextCurrentQuestionId,
        });
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      logger.error("Homepage footer feedback error", { error, type });
    }

    // Open detailed feedback modal with the selected rating
    setIsFeedbackModalOpen(true);

    // Reset feedback given state after 3 seconds
    setTimeout(() => {
      setFeedbackGiven(null);
    }, 3000);
  };

  // Vérifier si l'écran de bienvenue doit être affiché (une fois par 24h)
  useEffect(() => {
    const checkWelcomeScreen = () => {
      if (typeof window === "undefined" || welcomeCheckedRef.current) return;

      welcomeCheckedRef.current = true;

      // Skip welcome screen if URL parameters are present (direct link with prefilled answers)
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = Array.from(urlParams.entries()).length > 0;

      if (hasUrlParams) {
        setShowWelcome(false);
        dismissWelcomeScreen();

        return;
      }

      const lastWelcomeShown = localStorage.getItem("crowdsec-welcome-shown");
      const now = Date.now();
      const twentyFourHours = 3 * 60 * 1000; // 3 minutes en millisecondes

      if (
        !lastWelcomeShown ||
        now - parseInt(lastWelcomeShown) > twentyFourHours
      ) {
        setShowWelcome(true);
        localStorage.setItem("crowdsec-welcome-shown", now.toString());
      } else {
        setShowWelcome(false);
        dismissWelcomeScreen();
      }
    };

    checkWelcomeScreen();
  }, [dismissWelcomeScreen]);

  useEffect(() => {
    if (contextCurrentQuestionId) {
      const illustrationPath = `/illustrations/questions/default.png`;

      setCurrentIllustration(illustrationPath);
    } else {
      setCurrentIllustration(null);
    }
  }, [contextCurrentQuestionId]);

  useEffect(() => {
    if (showWelcome && getStartedButtonRef.current) {
      getStartedButtonRef.current.focus();
    }
  }, [showWelcome]);

  useEffect(() => {
    if (showWelcome) {
      const timer = setTimeout(() => {
        setIsWelcomeExiting(true);
        setTimeout(() => {
          setShowWelcome(false);
          dismissWelcomeScreen();
        }, 200);
      }, 6000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [showWelcome, dismissWelcomeScreen]);

  useEffect(() => {
    sendPageView({
      page: typeof window !== "undefined" ? window.location.href : undefined,
      pageTitle: "CrowdSec Onboarding Home",
      firstSource:
        typeof document !== "undefined" ? document.referrer : undefined,
    });
  }, []);

  const handleGetStarted = () => {
    setIsWelcomeExiting(true);
    setTimeout(() => {
      setShowWelcome(false);
      dismissWelcomeScreen();
    }, 800);
  };

  return (
    <div className="flex flex-col lg:flex-row w-full relative h-full">
      {/* Welcome Overlay */}
      {showWelcome && (
        <div
          className={cn(
            "absolute inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 h-screen",
            isWelcomeExiting && "animate-fadeOut",
          )}
        >
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(29,78,216,0.15),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(147,51,234,0.1),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(59,130,246,0.1),transparent_50%)]" />
          </div>

          <div className="relative max-w-4xl mx-auto px-8 text-center flex flex-col items-center">
            {/* Logo */}
            <div className="mb-12 opacity-0 animate-logoGlow">
              <div className="relative">
                <div className="w-24 h-24 mx-auto mb-6 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 rounded-2xl opacity-80 animate-pulse" />
                  <div className="absolute inset-1 bg-slate-900 rounded-xl flex items-center justify-center">
                    <Image
                      alt="CrowdSec Logo"
                      className="w-16 h-12"
                      height={48}
                      src="/illustrations/AlpacasFichier 39.png"
                      width={48}
                    />
                  </div>
                </div>
                <div className="text-2xl font-bold text-white tracking-wide">
                  CrowdSec
                </div>
              </div>
            </div>

            {/* Main title */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 opacity-0 animate-textDelay1 leading-tight">
              Navigate the{" "}
              <span className="bg-gradient-to-r from-indigo-800 via-indigo-500 to-amber-500 bg-clip-text text-transparent">
                CrowdSec
              </span>{" "}
              Universe
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-gray-300 mb-12 opacity-0 animate-textDelay2 max-w-3xl leading-relaxed">
              Discover the perfect security setup for YOUR infrastructure. Smart
              recommendations, faster deployment.
            </p>

            {/* CTA Button */}
            <Button
              ref={getStartedButtonRef}
              className={cn(
                "opacity-0 animate-buttonDelay bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-4 px-12 text-lg rounded-xl transition-all duration-300 shadow-2xl hover:shadow-blue-500/25 border-0",
              )}
              size="lg"
              variant="solid"
              onPress={handleGetStarted}
            >
              Discover your top suggestions now!
            </Button>

            {/* Features */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 opacity-0 animate-textDelay3">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      fillRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-2">
                  Smart Discovery
                </h3>
                <p className="text-gray-400 text-sm">
                  Find exactly what your infrastructure needs
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-2">Your Roadmap</h3>
                <p className="text-gray-400 text-sm">
                  Personalized path through CrowdSec&apos;s ecosystem
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-pink-500 to-red-600 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      fillRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-2">Deploy Faster</h3>
                <p className="text-gray-400 text-sm">
                  From exploration to protection in minutes
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header à gauche */}
      <div className="lg:fixed lg:w-[33%] w-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-950 to-slate-800 lg:h-[calc(100vh-64px)] relative overflow-auto">
        {/* Background pattern for left panel */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(29,78,216,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(147,51,234,0.08),transparent_50%)]" />
        </div>

        <div className="flex flex-col items-center justify-center grow p-8 space-y-5 relative z-10">
          {/* Hide LiveSummary if user is on the very first question */}
          {(() => {
            const firstQuestion = findFirstQuestion(userData);

            return !(contextCurrentQuestionId === firstQuestion?.id);
          })() && <LiveSummary questions={userData.questions} />}
          <div className="hidden md:block">
            {currentIllustration && (
              <Image
                key={currentIllustration} // Utiliser currentIllustration qui est dans l'état local
                alt="Illustration for current question"
                className={cn("mb-8 animate-fadeIn drop-shadow-lg")}
                height={90}
                src={currentIllustration} // Utiliser currentIllustration qui est dans l'état local
                width={130}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/illustrations/questions/default.png";
                }}
              />
            )}
          </div>
          {Object.keys(contextAnswers).length === 0 && (
            <>
              <h1
                className={cn(
                  "text-3xl md:text-2xl font-bold text-white text-center drop-shadow-lg",
                )}
              >
                2 min to power up with CrowdSec
              </h1>
              <p
                className={cn(
                  "text-base md:text-medium text-gray-300 mt-2 text-center w-7/8 drop-shadow-sm",
                )}
              >
                Answer a few quick questions (2 min max) to get the best
                protection and tools for you. No technical skills needed!
              </p>
            </>
          )}
        </div>
        <footer
          className={cn(
            "hidden lg:block w-full backdrop-blur-sm p-6 text-center border-t border-white/5 z-10",
          )}
        >
          <div
            className={cn(
              "flex items-center justify-between text-sm text-gray-400/80",
            )}
          >
            {/* Left side - Copyright */}
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
              <span className="font-medium tracking-wide">
                CrowdSec {new Date().getFullYear()}
              </span>
              <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
            </div>

            {/* Right side - Discrete feedback */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {feedbackGiven
                  ? feedbackGiven === "positive"
                    ? "Thanks! 😊"
                    : "Thanks! 💪"
                  : "Was this helpful?"}
              </span>
              <div className="flex items-center gap-1">
                <Tooltip
                  content={
                    feedbackGiven === "positive" ? "Thanks!" : "Yes, helpful"
                  }
                  placement="top"
                >
                  <Button
                    isIconOnly
                    className={`w-6 h-6 min-w-0 transition-all duration-200 ${
                      feedbackGiven === "positive"
                        ? "bg-success/20 text-success scale-110"
                        : "hover:bg-success/10 hover:text-success text-gray-400 hover:scale-110"
                    }`}
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleFeedbackClick("positive")}
                  >
                    <HandThumbsUp className="w-2.5 h-2.5" />
                  </Button>
                </Tooltip>

                <Tooltip
                  content={
                    feedbackGiven === "negative"
                      ? "Thanks for the feedback!"
                      : "No, not helpful"
                  }
                  placement="top"
                >
                  <Button
                    isIconOnly
                    className={`w-6 h-6 min-w-0 transition-all duration-200 ${
                      feedbackGiven === "negative"
                        ? "bg-warning/20 text-warning scale-110"
                        : "hover:bg-warning/10 hover:text-warning text-gray-400 hover:scale-110"
                    }`}
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleFeedbackClick("negative")}
                  >
                    <HandThumbsDown className="w-2.5 h-2.5" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </footer>
      </div>

      <FeedbackModal
        initialRating={modalInitialRating}
        isOpen={isFeedbackModalOpen}
        userJourney={{
          answers: contextAnswers,
          currentQuestionId: contextCurrentQuestionId,
          progressPercent: Object.keys(contextAnswers).length * 10,
        }}
        onCloseAction={() => setIsFeedbackModalOpen(false)}
      />

      <OnboardingWizard />
    </div>
  );
}

export default ClientHomePage;
